'use client';

import React, { CSSProperties, JSX } from 'react';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';

import { toZonedTime } from 'date-fns-tz';

import { CAL_DISPLAY_TYPE_MOBILE } from '@/constants/calendar';
import {
	ICON_CALENDAR,
	ICON_CURRENCY,
	ICON_FORMAT,
	ICON_LOCATION,
	ICON_PLAY,
	ICON_VIEW,
} from '@/constants/icons';
import {
	TX_EVENT_REGISTER,
	TX_FREE,
} from '@/constants/strings';
import {
	URL_ADMIN_EVENTS_VIEW,
	URL_ADMIN_EVENTS_SCHEDULE_VIEW,
	URL_BUY_PRODUCT_PAGE,
} from '@/constants/urls';

import useStore from '@/hooks/useStore';

import Location from '@/models/locations/Location';
import StoreEvent from '@/models/events/StoreEvent';

import { encodeUrlDate } from '@/utils/events';
import { 
	breaksToBrs,
	formatPrice, 
	stringFormat, 
} from '@/utils/formatting';
import { getStoreTimeZone } from '@/utils/general';
import { getStoreLanguage } from '@/utils/language';

import ActionButton from '@/components/Buttons/ActionButton';
import Icon from '@/components/Icons/Icon';

import styles from './_styles/EventCalendarEventSummary.module.scss';


// Prop types
interface EventCalendarEventSummaryProps {
	adminTheme: boolean;
	closeAction: () => void;
	date: Date | null;
	displayMode: string;
	eventObj: StoreEvent | null;
}

export const EventCalendarEventSummary = (props: EventCalendarEventSummaryProps): JSX.Element => {
  
	// Props
	const {
		adminTheme,
		closeAction,
		date,
		displayMode,
		eventObj,
	} = props;

	// Internationalization
	const { 
		i18n,
		t, 
	} = useTranslation();

	// Store
	const { store } = useStore();

	// Constants
	const descriptionBreakStyle: CSSProperties = { margin: '0 0 0.5em 0' };

	const storeLang = getStoreLanguage(i18n);

	const isMobileView = displayMode === CAL_DISPLAY_TYPE_MOBILE;

	// Methods
	const getRootClass = (): string => {
		
		const baseClass = styles.EventCalendarEventSummary;
		const adminClass = adminTheme ? styles.adminTheme : '';
		const mobileClass = isMobileView ? styles.mobileView : '';

		return `${baseClass} ${adminClass} ${mobileClass}`;
	}

	const getLinkUrl = (): string => {

		if(!eventObj || !date) { return ''; }

		if(eventObj?.isRepeating) {
			return stringFormat(URL_ADMIN_EVENTS_SCHEDULE_VIEW, { date: encodeUrlDate(date), publicUuid: eventObj?.schedule?.publicUuid });
		} else {
			return stringFormat(URL_ADMIN_EVENTS_VIEW, { publicUuid: eventObj.publicUuid });
		}
	}

	const getLocation = (): Location | null => {
		if(eventObj?.location) {
			return eventObj.location;
		} else if(store) {
			return store.locations.find(sl => sl.isDefault) || null;
		}
		return null;
	}

	// Render
	return (
		<div className={getRootClass()}>
			<div className={styles.eceLiner}>
				<div className={styles.eceHeaderWrapper}>
					<div className={styles.eceHeader}>
						{adminTheme && (
							<Link href={getLinkUrl()}>
								<div className={styles.eceIconWrapper}>
									<Icon value={ICON_VIEW} iconClass={styles.eceIcon} />
								</div>
							</Link>
						)}
						<div className={styles.eceIconWrapper} onClick={closeAction}>
							<div className={`${styles.textWrapper} FlexCenter`}>&times;</div>
						</div>
					</div>
				</div>
				<div className={styles.eceBodyScrollWrapper}>
					<div className={styles.eceBodyWrapper}>
						{eventObj && (
							<div className={styles.eceBody}>
								<div className={styles.titleWrapper}>
									<div className={styles.nameWrapper}>{eventObj.name}</div>
									<div className={styles.timeWrapper}>
										{toZonedTime(eventObj.startTime, getStoreTimeZone()).toLocaleString(
											storeLang, 
											{
												month: 'long', 
												day: 'numeric', 
												hour: 'numeric',
												 minute: 'numeric',
											}
										)}
										</div>
								</div>
								{(!adminTheme && eventObj.hasLinkedProduct && eventObj.linkedProductPermalink && eventObj.productLine.permalink && eventObj.registrationWindowState === 'open') && (
									<div className={styles.registerWrapper}>
										<ActionButton
											fullWidth
											linkTo={stringFormat(URL_BUY_PRODUCT_PAGE, {
												productLine: eventObj.productLine.permalink,
												permalink: eventObj.linkedProductPermalink,
											})}>
											{t(TX_EVENT_REGISTER)}
										</ActionButton>
									</div>
								)}
								<div className={styles.configWrapper}>
									<div className={styles.configDataWrapper}>
										<div className={styles.configIconWrapper}>
											<Icon value={ICON_PLAY} />
										</div>
										<div className={styles.configLabelWrapper}>
											{eventObj.productLine.name || ''}
										</div>
									</div>
									{(eventObj.format.name && eventObj.productLine.eventFormats.length > 0) && (
										<div className={styles.configDataWrapper}>
											<div className={styles.configIconWrapper}>
												<Icon value={ICON_FORMAT} />
											</div>
											<div className={styles.configLabelWrapper}>
												{t(eventObj.format.name)}
											</div>
										</div>
									)}
									<div className={styles.configDataWrapper}>
										<div className={styles.configIconWrapper}>
											<Icon value={ICON_CURRENCY} />
										</div>
										<div className={styles.configLabelWrapper}>
											{formatPrice(
												eventObj.cost, 
												{ 
													language: storeLang, 
													zeroValue: t(TX_FREE),
												}
											)}
										</div>
									</div>
									<div className={styles.configDataWrapper}>
										<div className={styles.configIconWrapper}>
											<Icon value={ICON_CALENDAR} />
										</div>
										<div className={styles.configLabelWrapper}>
											{toZonedTime(eventObj.startTime, getStoreTimeZone()).toLocaleString(
												storeLang, 
												{ 
													month: 'long', 
													day: 'numeric', 
													hour: 'numeric', 
													minute: 'numeric', 
												}
											)}
										</div>
									</div>
									{getLocation() && (
										<div className={styles.configDataWrapper}>
											<div className={styles.configIconWrapper}>
												<Icon value={ICON_LOCATION} />
											</div>
											<div className={styles.configLabelWrapper}>
												{(getLocation() as Location).address.formatDefault({ includePhone: true }).map((addLine, i) => {
													return <div key={i} className={styles.labelLine}>{addLine}</div>
												})}
											</div>
										</div>
									)}
								</div>
								{eventObj.description && (
									<div
										className={styles.descWrapper}
										dangerouslySetInnerHTML={{
											__html: breaksToBrs(eventObj.description, { activateLinks: true, ...descriptionBreakStyle })
										}} />
								)}
							</div>
						)}
					</div>
				</div>
				<div className={styles.eceFooterWrapper}>
					<div className={styles.eceFooter} />
				</div>
			</div>
		</div>
	);
};

export default EventCalendarEventSummary;
